/** The takeoff toolbar, as data.
 *
 *  Three consumers that MUST agree: the web rail, the native rail (both draw the shared
 *  TakeoffToolRail) and the `takeoff:tools` payload the native shells render their own
 *  bottom bar from. They used to be hand-written lists and had already drifted (Cutout sat
 *  after Volume on web but after Distance on native). Ordering, grouping and enablement
 *  come from here, so that cannot happen again by construction.
 *
 *  Descriptors, not JSX: icons are path data (toolGlyphs) so React Native can draw them,
 *  and enablement is a predicate over a context object rather than baked into call sites.
 *  The web-only pieces (DOM icon, anchor rect) live in features/takeoff/tools/toolbarRegistry.
 */

import type { ActiveTool } from '../../types/takeoff';
import type { AdskGlyphName } from './adskGlyphName';
import type { ToolGlyphName } from './toolGlyphs';

/** Everything enablement can depend on. Passed whole so a new tool can gate on
 *  something new without every call site changing. */
export interface ToolbarContext {
  calibrated: boolean;
  /** The selected measurement is an area or volume — Cutout needs a target. */
  cutoutTargetSelected: boolean;
  canOpen3D: boolean;
  /** The 3D viewport is up — gates tools that only exist there (Orbit). */
  is3D: boolean;
  /** The sheet is rendered by Autodesk's viewer rather than the built-in
   *  canvas. Its own settings panel is only reachable from there. */
  isApsViewer?: boolean;
  /** The open sheet has at least one retained earlier revision, so there is
   *  something to compare it against. Without a chain the compare entry would
   *  open a dialog with nothing in it. */
  hasOlderRevisions?: boolean;
}

/** Visual grouping. Rendered as separators on web and as `section` on native.
 *  A tool's group is what it DOES, not where it happens to sit:
 *   - navigate: moves the camera or sets up the sheet; never produces a value
 *   - measure:  produces a quantity that reaches the BOQ
 *   - markup:   internal annotation, never a quantity
 */
export type ToolGroup = 'navigate' | 'measure' | 'markup';

const GROUP_ORDER: ToolGroup[] = ['navigate', 'measure', 'markup'];

export interface ToolbarEntry {
  id: ActiveTool;
  label: string;
  group: ToolGroup;
  /** Names the glyph in toolGlyphs (not the tool: `label` already says that). */
  glyph: ToolGlyphName;
  /** Defaults to always-enabled. */
  enabled?: (ctx: ToolbarContext) => boolean;
  /** Defaults to always-shown. Unlike `enabled` (greyed out), an invisible
   *  tool does not exist in the current view at all — Orbit has no meaning
   *  over a flat sheet, so it is absent there rather than disabled. */
  visible?: (ctx: ToolbarContext) => boolean;
  /** Keyboard hint appended to the web tooltip only — native draws its own. */
  shortcut?: string;
  /** Autodesk's glyph for the same tool, shown instead of the glyph on the
   *  Autodesk surface (see ToolIcon). Only for tools Autodesk draws there;
   *  a tool with no Autodesk equivalent keeps the shared icon everywhere. */
  apsIcon?: AdskGlyphName;
}

/** The tools rail, in render order. */
export const TOOLBAR_ENTRIES: ToolbarEntry[] = [
  { id: 'select',       label: 'Select',      group: 'navigate', glyph: 'select', shortcut: 'V', apsIcon: 'selection' },
  { id: 'pan',          label: 'Pan',         group: 'navigate', glyph: 'pan', apsIcon: 'pan' },
  // 3D only: sets what a PLAIN drag does there (Pan slides, Orbit turns).
  // Either way the other gesture stays on right/middle/shift-drag.
  { id: 'orbit',        label: 'Orbit',       group: 'navigate', glyph: 'orbit', apsIcon: 'orbit-constrained',
    visible: c => c.is3D },
  { id: 'zoomWindow',   label: 'Zoom Window', group: 'navigate', glyph: 'zoomWindow', shortcut: 'W', apsIcon: 'zoom' },
  // Calibrate is deliberately NOT a rail tool (#931). A sheet has one scale,
  // and it had two controls: this row and the scale picker, which differed
  // only in that one of them offered the presets 97.7% of calibrations land
  // on. Every scale control now carries "Custom, measure a known distance",
  // which arms the same two-click flow through takeoff:calibrate — the tool
  // itself, its hint pill and its pins are untouched in tools/calibrate.tsx.

  // One Area tool on every surface: click per vertex, or a drag for a
  // rectangle. The built-in canvas does the rectangle on a long-press drag;
  // the Autodesk viewer's Edit2D polygon tool does it on a plain drag.
  { id: 'area',         label: 'Area',        group: 'measure',  glyph: 'area',    enabled: c => c.calibrated, apsIcon: 'measure-area-new' },
  // Linear is a polyline. Autodesk's font has no polyline glyph, and its ruler
  // read as a plain length measure next to Distance, so Linear keeps the
  // shared polyline glyph on the Autodesk surface too.
  { id: 'length',       label: 'Linear',      group: 'measure',  glyph: 'length',  enabled: c => c.calibrated },
  // Volume is deliberately absent: it drew exactly like Area and differed only
  // in the unit, which is a property of the ROW (its depth), not of how the
  // shape was drawn — hence it committed at value 0 until a depth was typed in
  // afterwards. Set a depth on an Area row and it reports m³. The tool
  // implementation and the 'volume' measurement type both remain, because
  // stored measurements and existing takeoff types still carry them.
  // Count needs no scale: a tally is a number of things, not a size.
  { id: 'count',        label: 'Count',       group: 'measure',  glyph: 'count' },
  { id: 'quickMeasure', label: 'Distance',    group: 'measure',  glyph: 'quickMeasure',   enabled: c => c.calibrated, apsIcon: 'measure-distance-new' },
  // A readout, never persisted: an angle needs no scale, so it is not gated on
  // calibration like the other measure tools. Autodesk's Measure extension
  // draws it, which is why it exists only on that surface.
  { id: 'angle',        label: 'Angle',       group: 'measure',  glyph: 'angle',   visible: c => !!c.isApsViewer, apsIcon: 'measure-angle-new' },
  // Cutout sits last because it is the only one that edits an existing shape
  // rather than drawing a new one — it is unreachable without an area or
  // volume already selected.
  { id: 'cutout',       label: 'Cutout',      group: 'measure',  glyph: 'cutout',
    enabled: c => c.calibrated && c.cutoutTargetSelected },

  { id: 'cloud',        label: 'Cloud',       group: 'markup',   glyph: 'cloud' },
  { id: 'callout',      label: 'Callout',     group: 'markup',   glyph: 'callout' },
  { id: 'textNote',     label: 'Text',        group: 'markup',   glyph: 'textNote' },
  { id: 'arrow',        label: 'Arrow',       group: 'markup',   glyph: 'arrow', apsIcon: 'arrow' },
  // A dimension's label is the measured distance unless overridden, so it
  // needs a scale the same way the measure-group tools do.
  { id: 'dimension',    label: 'Dimension',   group: 'markup',   glyph: 'dimension', enabled: c => c.calibrated },
];

export function isToolEnabled(entry: ToolbarEntry, ctx: ToolbarContext): boolean {
  return entry.enabled ? entry.enabled(ctx) : true;
}

export function isToolVisible(entry: ToolbarEntry, ctx: ToolbarContext): boolean {
  return entry.visible ? entry.visible(ctx) : true;
}

/** Entries split into contiguous groups, in GROUP_ORDER, with empties dropped.
 *  The web rail draws a separator between groups; nothing else should be
 *  deciding where the dividers go. */
export function toolbarGroups(ctx?: ToolbarContext): ToolbarEntry[][] {
  return GROUP_ORDER
    .map(g => TOOLBAR_ENTRIES.filter(e => e.group === g && (!ctx || isToolVisible(e, ctx))))
    .filter(group => group.length > 0);
}

// ── The view rail — not cursor tools ────────────────────────────────────────
// A view-mode switch and a panel toggle. Kept apart from the tools so they
// don't read as things you draw with. Summary is web-only: the native shells
// present the schedule their own way, so it is deliberately absent from the
// bridge payload rather than sent and ignored.

export interface ViewRailEntry {
  id: 'view3d' | 'summary' | 'viewerSettings' | 'fitToView' | 'textSearch' | 'compareRevisions';
  /** Label when inactive / active — both rails' entries are toggles. */
  label: (active: boolean) => string;
  glyph: ToolGlyphName;
  enabled?: (ctx: ToolbarContext) => boolean;
  /** Defaults to always-shown, like ToolbarEntry.visible. */
  visible?: (ctx: ToolbarContext) => boolean;
  /** Whether the native bottom bar renders it. */
  native: boolean;
  /** As ToolbarEntry.apsIcon. */
  apsIcon?: AdskGlyphName;
}

export const VIEW_RAIL_ENTRIES: ViewRailEntry[] = [
  { id: 'view3d',  label: a => (a ? '2D View' : '3D View'),          glyph: 'view3d',   enabled: c => c.canOpen3D, native: true, apsIcon: 'box' },
  { id: 'summary', label: a => (a ? 'Hide summary' : 'Show summary'), glyph: 'summary', native: false },
  // An action, not a toggle: both surfaces can fit the sheet to the view
  // (the F key already does), so it shows everywhere. The native shells fit
  // through their own bars.
  { id: 'fitToView', label: () => 'Fit to view', glyph: 'fitToView', native: false, apsIcon: 'fit-to-view' },
  // Autodesk reads the sheet's text (the PDF loader's string table), so a
  // label can be found and counted; the built-in canvas has no text layer.
  { id: 'textSearch', label: () => 'Find text', glyph: 'textSearch', native: false,
    visible: c => !!c.isApsViewer },
  // Two panes side by side need two sheets live at once, which only the
  // Autodesk surface can do (the built-in canvas renders one sheet per view).
  // Hidden without a retained earlier revision: an entry that opens an empty
  // dialog is worse than no entry. Autodesk's icon font has no compare glyph,
  // so this keeps the shared one on every surface.
  { id: 'compareRevisions', label: () => 'Compare revisions', glyph: 'compareRevisions', native: false,
    visible: c => !!c.isApsViewer && !!c.hasOlderRevisions },
  // Autodesk's own toolbar is hidden on that surface, so this is the only
  // way to its settings panel (2D sheet colour, display units, precision,
  // navigation). The macOS shell reaches it through its settings menu.
  { id: 'viewerSettings', label: () => 'Settings', glyph: 'viewerSettings', native: false, apsIcon: 'settings',
    visible: c => !!c.isApsViewer },
];

export function isViewEntryVisible(entry: ViewRailEntry, ctx: ToolbarContext): boolean {
  return entry.visible ? entry.visible(ctx) : true;
}

// ── Native bridge payload ───────────────────────────────────────────────────

export interface NativeToolDescriptor {
  id: string;
  label: string;
  enabled: boolean;
  section: number;
  isToggle?: boolean;
  active?: boolean;
}

/** The `takeoff:tools` payload, derived from the same entries the web rail
 *  draws. `section` is the group's index, so the native bar's dividers land
 *  in the same places as the web separators.
 *
 *  Markup ids the shell does not know fall back to a generic glyph and
 *  dispatch straight back to the web handler, so adding a tool here needs no
 *  Swift change for it to work. */
export function nativeToolPayload(
  ctx: ToolbarContext,
  view: { is3D: boolean },
): NativeToolDescriptor[] {
  const groups = toolbarGroups(ctx);
  const tools: NativeToolDescriptor[] = groups.flatMap((group, section) =>
    group.map(entry => ({
      id: entry.id,
      label: entry.label,
      enabled: isToolEnabled(entry, ctx),
      section,
    })),
  );

  const viewSection = groups.length;
  for (const entry of VIEW_RAIL_ENTRIES) {
    if (!entry.native) continue;
    tools.push({
      id: entry.id,
      // The native bar is tight, so the 3D toggle uses the short form.
      label: entry.id === 'view3d' ? (view.is3D ? '2D' : '3D') : entry.label(false),
      enabled: entry.enabled ? entry.enabled(ctx) : true,
      section: viewSection,
      isToggle: true,
      active: entry.id === 'view3d' ? view.is3D : false,
    });
  }
  return tools;
}
